import { Stack, useTheme } from "@mui/material";
import { type FC, useMemo } from "react";
import type { Control } from "react-hook-form";
import { useDocumentListQuery } from "web_myplann/Api/Document/useDocumentListQuery";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { ContractFormPicker } from "web_myplann/Portfolio/Components/Redesign/Documents/ContractFormPicker";
import { FolderFormPicker } from "web_myplann/Portfolio/Components/Redesign/Documents/FolderFormPicker";
import type {
	DocumentUploadFormInput,
	DocumentUploadFormOutput,
} from "web_myplann/Portfolio/Forms/DocumentUploadForm/documentUploadFormSchema";
import { useLinkableItemOptions } from "web_myplann/Portfolio/Hooks/useLinkableItemOptions";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowSectionTitle } from "web_myplann/Shared/Components/FlatRow/FlatRowSectionTitle";

const R = Resources.Portfolio.List.DocumentUpload;

type Props = {
	control: Control<DocumentUploadFormInput, unknown, DocumentUploadFormOutput>;
	showContractField: boolean;
};

export const DocumentUploadFormFields: FC<Props> = ({
	control,
	showContractField,
}) => {
	const { palette } = useTheme();
	const { t } = useResource();

	const { data: documentList } = useDocumentListQuery();
	const linkableOptions = useLinkableItemOptions(showContractField);
	const folders = useMemo(
		() => (documentList?.tags ?? []).map((tag) => tag.title),
		[documentList?.tags],
	);

	return (
		<>
			<FlatRowSectionTitle title={t(R.detailSection)} />
			<Stack bgcolor={palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="name"
					label={t(R.name)}
					placeholder={t(R.namePlaceholder)}
				/>
				<FolderFormPicker
					control={control}
					name="folder"
					label={t(R.folder)}
					placeholder={t(R.folderPlaceholder)}
					folders={folders}
					isLast={!showContractField}
				/>
				{showContractField && (
					<ContractFormPicker
						control={control}
						name="linkedItem"
						label={t(R.contract)}
						placeholder={t(R.contractPlaceholder)}
						options={linkableOptions}
						isLast
					/>
				)}
			</Stack>
		</>
	);
};
